/**
 * Theme pure helpers — Phase 1a
 *
 * No React, no side effects beyond the explicit DOM write in applyTheme.
 * All functions are safe to call from the inline bootstrap script, from
 * useTheme, or from tests.
 *
 * Storage key: 'pia-theme'
 */

import type { ThemePreference, ResolvedTheme } from '../types/theme.js';

const STORAGE_KEY = 'pia-theme';
const VALID_PREFS: ThemePreference[] = ['light', 'dark', 'system'];

/**
 * Read the stored theme preference from localStorage.
 *
 * Falls back to 'system' if:
 *   - localStorage is empty or the key is absent
 *   - The stored value is not a valid ThemePreference
 *   - localStorage.getItem throws (Safari private mode / sandboxed iframe)
 */
export function readStoredPreference(): ThemePreference {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (raw !== null && (VALID_PREFS as string[]).includes(raw)) {
			return raw as ThemePreference;
		}
		return 'system';
	} catch {
		return 'system';
	}
}

/**
 * Resolve the preference to a concrete theme ('light' or 'dark').
 *
 * @param pref - The stored preference. 'light' and 'dark' are returned as-is.
 *   'system' delegates to the OS.
 * @param systemDark - Optional explicit OS dark-mode state. When provided,
 *   matchMedia is not called — this keeps tests simple and deterministic.
 *   When omitted, we read window.matchMedia('(prefers-color-scheme: dark)').
 */
export function resolveTheme(
	pref: ThemePreference,
	systemDark?: boolean,
): ResolvedTheme {
	if (pref === 'light' || pref === 'dark') return pref;

	// pref === 'system' — honour OS preference
	const dark =
		systemDark ??
		(typeof window !== 'undefined' &&
			window.matchMedia?.('(prefers-color-scheme: dark)').matches) ??
		false;

	return dark ? 'dark' : 'light';
}

/**
 * Write the resolved theme to document.documentElement.dataset.theme.
 * This is the single DOM-write point so effects and tests can spy on it.
 */
export function applyTheme(resolved: ResolvedTheme): void {
	document.documentElement.dataset.theme = resolved;
}

/**
 * Density helpers re-exported here so useTheme.ts has a single import point
 * for all theme and density utilities. Canonical implementations live in
 * lib/density.ts — this re-export is the public interface.
 */
export { readStoredDensity, applyDensity } from './density.js';
